import { FieldOptionBase } from 'module/api/field/types';
import { UserStatus } from 'module/api/user/types';
import { ClassNameOnlyProps } from 'module/types/common';
import { Select } from '@rocketlane/ui';
import { FC, ReactNode, RefObject } from 'react';
import { Popover } from './components';
import { renderFieldEditorUserAvatar } from './field-editor-user-avatar';
import { FieldEditorSelectMode, OptionsType, ValueType } from '../types';
import type { SelectProps } from '@rocketlane/ui';
import type {
  GroupBase,
  SelectComponentsConfig,
  SelectInstance,
} from 'react-select';

interface FieldEditorOption<OptionValueType extends number | string>
  extends FieldOptionBase<OptionValueType> {
  description?: ReactNode;
  imageUrl?: string;
  logoUrl?: string;
  userStatus?: UserStatus;
  extra?: {
    userStatus?: UserStatus;
  };
}

export type Props<
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
> = ClassNameOnlyProps & {
  title: ReactNode;
  value: ValueType<IsMulti, OptionValueType>;
  onChange: NonNullable<
    SelectProps<FieldEditorOption<OptionValueType>, IsMulti>['onChange']
  >;
  selectRef: RefObject<SelectInstance>;
  options: OptionsType<OptionValueType>;
  components: SelectComponentsConfig<
    FieldEditorOption<OptionValueType>,
    IsMulti,
    GroupBase<FieldEditorOption<OptionValueType>>
  >;
  isMulti: IsMulti;
  type: FieldEditorSelectMode;
  grouped?: boolean;
};

export const getControlledSelectBasedEditorPopover = <
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
>(): FC<React.PropsWithChildren<Props<IsMulti, OptionValueType>>> => {
  const ControlledSelectBasedEditorPopover: FC<
    React.PropsWithChildren<Props<IsMulti, OptionValueType>>
  > = ({
    title,
    onChange,
    selectRef,
    className,
    options,
    components,
    isMulti,
    value,
    type,
  }) => (
    <Popover className={className}>
      <Popover.Title>{title}</Popover.Title>
      <Select<FieldEditorOption<OptionValueType>, IsMulti>
        closeMenuOnSelect={!isMulti}
        collapsible={false}
        components={components}
        controlShouldRenderValue={type !== 'user'}
        hideSelectedOptions={false}
        isMulti={isMulti}
        menuFlush
        menuIsOpen
        menuPortalTarget={null}
        onChange={onChange}
        options={options}
        ref={selectRef}
        renderOptionIcon={
          type === 'user' ? renderFieldEditorUserAvatar : undefined
        }
        searchControl
        value={value}
        variant={type === 'tag' ? 'basic' : type}
        virtualized
      />
    </Popover>
  );

  return ControlledSelectBasedEditorPopover;
};
